<template>
  <div class="agent-management">
    <div class="page-header">
      <h1>代理商管理</h1>
      <div class="header-actions">
        <el-button type="primary" @click="showCreateDialog = true">
          <el-icon><Plus /></el-icon>
          添加代理商
        </el-button>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-number">{{ stats.total || 0 }}</div>
              <div class="stat-label">总代理商</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-number">{{ stats.active || 0 }}</div>
              <div class="stat-label">活跃代理商</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-number">{{ stats.total_quota || 0 }}</div>
              <div class="stat-label">总配额</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-number">{{ stats.used_quota || 0 }}</div>
              <div class="stat-label">已用配额</div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :model="filters" inline>
        <el-form-item label="搜索">
          <el-input v-model="filters.search" placeholder="用户名/姓名/邮箱" clearable />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="filters.status" placeholder="选择状态" clearable>
            <el-option label="活跃" value="active" />
            <el-option label="非活跃" value="inactive" />
            <el-option label="已暂停" value="suspended" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadAgents">搜索</el-button>
          <el-button @click="resetFilters">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 代理商列表 -->
    <el-card class="table-card">
      <el-table :data="agents" v-loading="loading" stripe>
        <el-table-column prop="username" label="用户名" width="120" />
        <el-table-column prop="real_name" label="真实姓名" width="100" />
        <el-table-column prop="email" label="邮箱" width="180" />
        <el-table-column prop="phone" label="手机号" width="120" />
        <el-table-column label="配额信息" width="200">
          <template #default="{ row }">
            <div>
              <div>总配额: {{ row.quota_total }}</div>
              <div>已用: {{ row.quota_used }}</div>
              <div>剩余: {{ row.quota_remaining }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="commission_rate" label="佣金比例" width="100">
          <template #default="{ row }">
            {{ (row.commission_rate * 100).toFixed(1) }}%
          </template>
        </el-table-column>
        <el-table-column label="卡密统计" width="120">
          <template #default="{ row }">
            <div>
              <div>生成: {{ row.cards_generated || 0 }}</div>
              <div>激活: {{ row.active_cards || 0 }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusColor(row.status)">
              {{ getStatusName(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="last_login_at" label="最后登录" width="160">
          <template #default="{ row }">
            {{ row.last_login_at ? formatDate(row.last_login_at) : '从未登录' }}
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="160">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="editAgent(row)">编辑</el-button>
            <el-button size="small" @click="resetPassword(row)">重置密码</el-button>
            <el-button 
              size="small" 
              :type="row.status === 'suspended' ? 'success' : 'warning'"
              @click="toggleAgentStatus(row)"
            >
              {{ row.status === 'suspended' ? '恢复' : '暂停' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.limit"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadAgents"
          @current-change="loadAgents"
        />
      </div>
    </el-card>

    <!-- 创建代理商对话框 -->
    <el-dialog v-model="showCreateDialog" title="添加代理商" width="600px">
      <el-form :model="createForm" :rules="createRules" ref="createFormRef" label-width="100px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="createForm.username" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model="createForm.password" type="password" placeholder="请输入密码" />
        </el-form-item>
        <el-form-item label="真实姓名" prop="real_name">
          <el-input v-model="createForm.real_name" placeholder="请输入真实姓名" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="createForm.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="createForm.phone" placeholder="请输入手机号" />
        </el-form-item>
        <el-form-item label="总配额" prop="quota_total">
          <el-input-number v-model="createForm.quota_total" :min="0" />
        </el-form-item>
        <el-form-item label="佣金比例" prop="commission_rate">
          <el-input-number v-model="createForm.commission_rate" :min="0" :max="1" :step="0.01" />
          <span style="margin-left: 10px;">（0-1之间的小数）</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showCreateDialog = false">取消</el-button>
        <el-button type="primary" @click="createAgent" :loading="creating">创建</el-button>
      </template>
    </el-dialog>

    <!-- 编辑代理商对话框 -->
    <el-dialog v-model="showEditDialog" title="编辑代理商" width="600px">
      <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="100px">
        <el-form-item label="用户名">
          <el-input v-model="editForm.username" disabled />
        </el-form-item>
        <el-form-item label="真实姓名" prop="real_name">
          <el-input v-model="editForm.real_name" placeholder="请输入真实姓名" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="editForm.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="editForm.phone" placeholder="请输入手机号" />
        </el-form-item>
        <el-form-item label="总配额" prop="quota_total">
          <el-input-number v-model="editForm.quota_total" :min="0" />
        </el-form-item>
        <el-form-item label="佣金比例" prop="commission_rate">
          <el-input-number v-model="editForm.commission_rate" :min="0" :max="1" :step="0.01" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="updateAgent" :loading="updating">更新</el-button>
      </template>
    </el-dialog>

    <!-- 重置密码对话框 -->
    <el-dialog v-model="showPasswordDialog" title="重置密码" width="400px">
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef" label-width="100px">
        <el-form-item label="代理商">
          <el-input v-model="currentAgent.username" disabled />
        </el-form-item>
        <el-form-item label="新密码" prop="new_password">
          <el-input v-model="passwordForm.new_password" type="password" placeholder="请输入新密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showPasswordDialog = false">取消</el-button>
        <el-button type="primary" @click="updatePassword" :loading="resetting">重置</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import { agentAPI } from '@/services/api'

export default {
  name: 'AgentManagement',
  components: {
    Plus
  },
  setup() {
    const loading = ref(false)
    const creating = ref(false)
    const updating = ref(false)
    const resetting = ref(false)
    const showCreateDialog = ref(false)
    const showEditDialog = ref(false)
    const showPasswordDialog = ref(false)
    const agents = ref([])
    const stats = ref({})
    const currentAgent = ref({})
    
    const filters = reactive({
      search: '',
      status: ''
    })
    
    const pagination = reactive({
      page: 1,
      limit: 20,
      total: 0
    })
    
    const createForm = reactive({
      username: '',
      password: '',
      real_name: '',
      email: '',
      phone: '',
      quota_total: 0,
      commission_rate: 0.1
    })
    
    const editForm = reactive({
      id: null,
      username: '',
      real_name: '',
      email: '',
      phone: '',
      quota_total: 0,
      commission_rate: 0.1
    })
    
    const passwordForm = reactive({
      new_password: ''
    })
    
    const createRules = {
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 3, max: 20, message: '用户名长度在3-20字符', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, max: 50, message: '密码长度在6-50字符', trigger: 'blur' }
      ],
      real_name: [
        { required: true, message: '请输入真实姓名', trigger: 'blur' }
      ],
      email: [
        { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
      ]
    }
    
    const editRules = {
      real_name: [
        { required: true, message: '请输入真实姓名', trigger: 'blur' }
      ],
      email: [
        { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
      ]
    }
    
    const passwordRules = {
      new_password: [
        { required: true, message: '请输入新密码', trigger: 'blur' },
        { min: 6, max: 50, message: '密码长度在6-50字符', trigger: 'blur' }
      ]
    }
    
    // 加载代理商列表
    const loadAgents = async () => {
      loading.value = true
      try {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          ...filters
        }
        const response = await agentAPI.getAgents(params)
        agents.value = response.data.agents
        pagination.total = response.data.pagination.total
      } catch (error) {
        ElMessage.error('加载代理商列表失败: ' + error.message)
      } finally {
        loading.value = false
      }
    }
    
    // 加载统计数据
    const loadStats = async () => {
      try {
        const response = await agentAPI.getAgentStats()
        stats.value = response.data
      } catch (error) {
        ElMessage.error('加载统计数据失败: ' + error.message)
      }
    }
    
    // 创建代理商
    const createAgent = async () => {
      creating.value = true
      try {
        await agentAPI.createAgent(createForm)
        ElMessage.success('代理商创建成功')
        showCreateDialog.value = false
        Object.assign(createForm, {
          username: '',
          password: '',
          real_name: '',
          email: '',
          phone: '',
          quota_total: 0,
          commission_rate: 0.1
        })
        loadAgents()
        loadStats()
      } catch (error) {
        ElMessage.error('创建代理商失败: ' + error.message)
      } finally {
        creating.value = false
      }
    }
    
    // 编辑代理商
    const editAgent = (agent) => {
      Object.assign(editForm, {
        id: agent.id,
        username: agent.username,
        real_name: agent.real_name,
        email: agent.email,
        phone: agent.phone,
        quota_total: agent.quota_total,
        commission_rate: agent.commission_rate
      })
      showEditDialog.value = true
    }
    
    // 更新代理商
    const updateAgent = async () => {
      updating.value = true
      try {
        const { id, ...updateData } = editForm
        // eslint-disable-next-line no-unused-vars
        const { username, ...finalUpdateData } = updateData
        await agentAPI.updateAgent(id, finalUpdateData)
        ElMessage.success('代理商信息更新成功')
        showEditDialog.value = false
        loadAgents()
      } catch (error) {
        ElMessage.error('更新代理商失败: ' + error.message)
      } finally {
        updating.value = false
      }
    }
    
    // 重置密码
    const resetPassword = (agent) => {
      currentAgent.value = agent
      passwordForm.new_password = ''
      showPasswordDialog.value = true
    }
    
    // 更新密码
    const updatePassword = async () => {
      resetting.value = true
      try {
        await agentAPI.resetAgentPassword(currentAgent.value.id, passwordForm)
        ElMessage.success('密码重置成功')
        showPasswordDialog.value = false
      } catch (error) {
        ElMessage.error('重置密码失败: ' + error.message)
      } finally {
        resetting.value = false
      }
    }
    
    // 切换代理商状态
    const toggleAgentStatus = async (agent) => {
      const newStatus = agent.status === 'suspended' ? 'active' : 'suspended'
      const action = newStatus === 'suspended' ? '暂停' : '恢复'
      
      try {
        await ElMessageBox.confirm(`确定要${action}代理商 ${agent.username} 吗？`, '确认操作')
        await agentAPI.updateAgentStatus(agent.id, { status: newStatus })
        ElMessage.success(`${action}成功`)
        loadAgents()
        loadStats()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(`${action}失败: ` + error.message)
        }
      }
    }
    
    // 重置筛选条件
    const resetFilters = () => {
      Object.assign(filters, {
        search: '',
        status: ''
      })
      pagination.page = 1
      loadAgents()
    }
    
    // 工具函数
    const getStatusName = (status) => {
      const names = {
        'active': '活跃',
        'inactive': '非活跃',
        'suspended': '已暂停'
      }
      return names[status] || status
    }
    
    const getStatusColor = (status) => {
      const colors = {
        'active': 'success',
        'inactive': 'info',
        'suspended': 'danger'
      }
      return colors[status] || ''
    }
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleString('zh-CN')
    }
    
    onMounted(() => {
      loadAgents()
      loadStats()
    })
    
    return {
      loading,
      creating,
      updating,
      resetting,
      showCreateDialog,
      showEditDialog,
      showPasswordDialog,
      agents,
      stats,
      currentAgent,
      filters,
      pagination,
      createForm,
      editForm,
      passwordForm,
      createRules,
      editRules,
      passwordRules,
      loadAgents,
      loadStats,
      createAgent,
      editAgent,
      updateAgent,
      resetPassword,
      updatePassword,
      toggleAgentStatus,
      resetFilters,
      getStatusName,
      getStatusColor,
      formatDate
    }
  }
}
</script>

<style scoped>
.agent-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.stats-cards {
  margin-bottom: 20px;
}

.stat-card {
  text-align: center;
}

.stat-content {
  padding: 20px;
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  color: #409eff;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.filter-card,
.table-card {
  margin-bottom: 20px;
}

.pagination-wrapper {
  margin-top: 20px;
  text-align: right;
}
</style>
